import { isArray } from '@ember/array';
import { assert } from '@ember/debug';
import { isPresent } from '@ember/utils';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
import { CatalogObjectType } from '@square/dashboard-items-data/models/connect/catalog-object';
import { Money } from '@square/dashboard-protos/models/squareup/common/currency';
import { SearchCatalogItemVariationsRequest, SearchCatalogItemVariationsResponse } from '@square/dashboard-protos/models/squareup/connect/v2/catalog/interfaces/search-catalog-item-variations';
import { CatalogItemProductType } from '@square/dashboard-protos/models/squareup/connect/v2/catalog/resources/enums';
import { getCookie } from '@square/dashboard-utilities/utils/cookies';
import Enum from '@square/dashboard-utilities/utils/enum';
import { baseConnectUrl } from '@square/dashboard-utilities/utils/url';
import { ajax, jsonDelete, jsonGet, jsonPost, jsonPut } from '@square/dashboard-utilities/utils/xhr';
import { camelize, underscore } from '@square/dashboard-utilities/utils/keys';
import { sendErrorToSentry } from '@square/dashboard-items-data/utils/sentry';
import CatalogError from '@square/dashboard-data/utils/catalog/catalog-error';
const CATALOG_CONNECT_V2_URL_PREFIX = '/v2/catalog';
const CATALOG_CONNECT_V2_FRONTEND_URL_PREFIX = '/v2/catalog/frontend';
export const SORT_ORDER = new Enum(['ASC', 'DESC']);
export const CATALOG_ITEM_PRODUCT_TYPE = new Enum(Object.keys(CatalogItemProductType));
export const SEARCH_CATALOG_IMAGES_FIELD = new Enum(['NAME', 'ITEM_NAME', 'IMAGE_CREATED_AT', 'IMAGE_UPDATED_AT']);
export const ITEM_ARCHIVED_STATE = new Enum(['ARCHIVED_STATE_DO_NOT_USE	', 'ARCHIVED_STATE_NOT_ARCHIVED', 'ARCHIVED_STATE_ARCHIVED	', 'ARCHIVED_STATE_ALL']);
export const CustomAttributeFilterNumberFilter = ProtoBaseModel.extend({
  min: attr(String),
  max: attr(String)
});
export const CustomAttributeFilter = ProtoBaseModel.extend({
  customAttributeDefinitionId: attr(String),
  key: attr(String),
  stringFilter: attr(String),
  selectionUidsFilter: repeated(String),
  boolFilter: attr(Boolean),
  numberFilter: attr(CustomAttributeFilterNumberFilter)
});
export const CatalogObjectBatch = ProtoBaseModel.extend({
  objects: attr()
});
const ConnectError = ProtoBaseModel.extend({
  code: attr(String),
  detail: attr(String),
  field: attr(String),
  category: attr(String)
});
const CatalogIdMapping = ProtoBaseModel.extend({
  clientObjectId: attr(String),
  objectId: attr(String)
});
const ListCatalogRequest = ProtoBaseModel.extend({
  types: repeated(CatalogObjectType),
  includeCounts: attr(Boolean),
  cursor: attr(String)
});
const ListCatalogResponse = ProtoBaseModel.extend({
  errors: repeated(ConnectError),
  objects: attr(),
  cursor: attr(String)
});
const RetrieveCatalogObjectRequest = ProtoBaseModel.extend({
  objectId: attr(String),
  includeCounts: attr(Boolean),
  includeRelatedObjects: attr(Boolean),
  catalogVersion: attr(Number),
  includeCategoryPathToRoot: attr(Boolean)
});
const RetrieveCatalogObjectResponse = ProtoBaseModel.extend({
  errors: repeated(ConnectError),
  object: attr(),
  relatedObjects: attr()
});
const UpsertCatalogObjectRequest = ProtoBaseModel.extend({
  idempotencyKey: attr(String),
  object: attr()
});
const UpsertCatalogObjectResponse = ProtoBaseModel.extend({
  errors: repeated(ConnectError),
  catalogObject: attr(),
  idMappings: repeated(CatalogIdMapping)
});
const BatchUpsertCatalogObjectsRequest = ProtoBaseModel.extend({
  idempotencyKey: attr(String),
  batches: repeated(CatalogObjectBatch)
});
const BatchUpsertCatalogObjectsResponse = ProtoBaseModel.extend({
  errors: repeated(ConnectError),
  objects: attr(),
  updatedAt: attr(String),
  idMappings: repeated(CatalogIdMapping)
});
const DeleteCatalogObjectRequest = ProtoBaseModel.extend({
  objectId: attr(String)
});
const DeleteCatalogObjectResponse = ProtoBaseModel.extend({
  errors: repeated(ConnectError),
  deleteObjectIds: repeated(String),
  deletedAt: attr(String)
});
const BatchDeleteCatalogObjectsRequest = ProtoBaseModel.extend({
  objectIds: repeated(String)
});
const BatchDeleteCatalogObjectsResponse = ProtoBaseModel.extend({
  errors: repeated(ConnectError),
  deletedObjectIds: repeated(String)
});
const BatchRetrieveCatalogObjectsRequest = ProtoBaseModel.extend({
  objectIds: repeated(String),
  includeCounts: attr(Boolean),
  includeRelatedObjects: attr(Boolean),
  includeDeletedObjects: attr(Boolean),
  includeCategoryPathToRoot: attr(Boolean)
});
const BatchRetrieveCatalogObjectsResponse = ProtoBaseModel.extend({
  errors: repeated(ConnectError),
  objects: attr(),
  relatedObjects: attr()
});
export const CatalogQueryExact = ProtoBaseModel.extend({
  attributeName: attr(String),
  attributeValue: attr(String)
});
export const CatalogQueryPrefix = ProtoBaseModel.extend({
  attributeName: attr(String),
  attributePrefix: attr(String)
});
export const CatalogQueryRange = ProtoBaseModel.extend({
  attributeName: attr(String),
  attributeMinValue: attr(Number),
  attributeMaxValue: attr(Number)
});
export const CatalogSortedAttributeQuery = ProtoBaseModel.extend({
  attributeName: attr(String),
  initialAttributeValue: attr(String),
  sortOrder: attr(SORT_ORDER)
});
export const CatalogSortedStringIntAttributeQuery = ProtoBaseModel.extend({
  attributeName: attr(String),
  attributeValue: attr(String),
  sortOrder: attr(SORT_ORDER)
});
export const CatalogQueryText = ProtoBaseModel.extend({
  keywords: repeated(String)
});
export const CatalogQueryPresentAtLocations = ProtoBaseModel.extend({
  locationIds: repeated(String)
});
export const CatalogCheckoutLinkForItemsQuery = ProtoBaseModel.extend({
  itemIds: repeated(String)
});
export const CatalogQuerySet = ProtoBaseModel.extend({
  attributeName: attr(String),
  attributeValues: repeated(String)
});
export const CatalogQueryDoesNotExist = ProtoBaseModel.extend({
  attributeName: attr(String)
});
export const CatalogQueryBooleanAttribute = ProtoBaseModel.extend({
  attributeName: attr(String),
  attributeValue: attr(Boolean)
});
export const CatalogQuery = ProtoBaseModel.extend({
  booleanAttributeQuery: repeated(CatalogQueryBooleanAttribute),
  doesNotExistQuery: attr(CatalogQueryDoesNotExist),
  exactQuery: attr(CatalogQueryExact),
  prefixQuery: attr(CatalogQueryPrefix),
  rangeQuery: attr(CatalogQueryRange),
  sortedAttributeQuery: attr(CatalogSortedAttributeQuery),
  textQuery: attr(CatalogQueryText),
  presentAtLocationsQuery: attr(CatalogQueryPresentAtLocations),
  checkoutLinkForItems: attr(CatalogCheckoutLinkForItemsQuery),
  setQuery: attr(CatalogQuerySet),
  sortedStringIntAttributeQuery: attr(CatalogSortedStringIntAttributeQuery)
  // There are many other query types that we haven't copied.
  // This definition can be expanded as new query types are needed.
  // prot: https://prototype.sqprod.co/#/docs/squareup.connect.v2.merchant_catalog.resources.CatalogQuery
});

// CATALOG IMAGE SEARCH

export const SearchCatalogImagesTextFilter = ProtoBaseModel.extend({
  fuzzy: attr(String),
  exact: attr(String)
});
export const SearchCatalogImagesFieldValue = ProtoBaseModel.extend({
  any: repeated(SEARCH_CATALOG_IMAGES_FIELD),
  all: repeated(SEARCH_CATALOG_IMAGES_FIELD),
  none: repeated(SEARCH_CATALOG_IMAGES_FIELD)
});
export const SearchCatalogImagesMultiFieldTextFilter = ProtoBaseModel.extend({
  query: attr(SearchCatalogImagesTextFilter),
  fields: attr(SearchCatalogImagesFieldValue)
});
export const SearchCatalogImagesFilter = ProtoBaseModel.extend({
  name: attr(SearchCatalogImagesTextFilter),
  itemName: attr(SearchCatalogImagesTextFilter),
  photoStudioOrderIdPresent: attr(Boolean),
  createdAt: attr(String),
  updatedAt: attr(String),
  multiField: attr(SearchCatalogImagesMultiFieldTextFilter)
});
export const CatalogImageQuerySort = ProtoBaseModel.extend({
  field: attr(SEARCH_CATALOG_IMAGES_FIELD),
  sortOrder: attr(SORT_ORDER)
});
export const CatalogImageQuery = ProtoBaseModel.extend({
  filter: attr(SearchCatalogImagesFilter),
  sort: attr(CatalogImageQuerySort)
});
const SearchCatalogObjectsRequest = ProtoBaseModel.extend({
  cursor: attr(String),
  objectTypes: repeated(String),
  includeDeletedObjects: attr(Boolean),
  includeRelatedObjects: attr(Boolean),
  beginTime: attr(String),
  query: attr(CatalogQuery),
  limit: attr(Number),
  includeCounts: attr(Boolean),
  includeCategoryPathToRoot: attr(Boolean)
});
const SearchCatalogObjectsResponse = ProtoBaseModel.extend({
  objects: attr(),
  relatedObjects: attr(),
  cursor: attr(String),
  errors: repeated(ConnectError)
});
const SearchCatalogItemsRequest = ProtoBaseModel.extend({
  textFilter: attr(String),
  categoryIds: repeated(String),
  itemIds: repeated(String),
  cursor: attr(String),
  enabledLocationIds: repeated(String),
  limit: attr(Number),
  productTypes: repeated(CATALOG_ITEM_PRODUCT_TYPE),
  sortOrder: attr(SORT_ORDER),
  customAttributeFilters: repeated(CustomAttributeFilter),
  nullFieldFilters: repeated(String),
  // Corresponding to SearchCatalogItemsRequest.NullableField Enums
  ecomVisibilities: repeated(String),
  stockLevels: repeated(String),
  soldOut: attr(Boolean),
  channels: repeated(String),
  archivedState: attr(String) // Corresponding to SearchCatalogItemsRequest.ArchivedState Enums
});
const SearchCatalogItemsResponse = ProtoBaseModel.extend({
  cursor: attr(String),
  errors: repeated(ConnectError),
  items: attr()
});
const SearchCatalogImagesRequest = ProtoBaseModel.extend({
  query: attr(CatalogImageQuery),
  cursor: attr(String),
  limit: attr(Number)
});
const SearchCatalogImagesResponse = ProtoBaseModel.extend({
  error: repeated(ConnectError),
  objects: repeated(),
  cursor: attr(String)
});
const CatalogAttributeValue = ProtoBaseModel.extend({
  stringValue: attr(String),
  intValue: attr(Number),
  boolValue: attr(Boolean),
  moneyValue: attr(Money)
});
const CatalogObjectChangeWrapper = {
  catalogObjectChangeClass: null,
  serialize(...args) {
    return this.catalogObjectChangeClass.serialize(...args);
  },
  deserialize(...args) {
    return this.catalogObjectChangeClass.deserialize(...args);
  }
};
const CatalogNestedChange = ProtoBaseModel.extend({
  id: attr(String),
  latestName: attr(String),
  createByUpdate: attr(Boolean),
  deletedByUpdate: attr(Boolean),
  attributeChanges: repeated(CatalogObjectChangeWrapper)
});
const CatalogAttributeChange = ProtoBaseModel.extend({
  attributeField: attr(String),
  customAttributeId: attr(String),
  attributeDisplayName: attr(String),
  attributeType: attr(String),
  previousValue: attr(CatalogAttributeValue),
  newValue: attr(CatalogAttributeValue),
  nestedChanges: repeated(CatalogNestedChange)
});
CatalogObjectChangeWrapper.catalogObjectChangeClass = CatalogAttributeChange;
const CatalogObjectChange = ProtoBaseModel.extend({
  objectId: attr(String),
  latestName: attr(String),
  objectType: attr(String),
  parentId: attr(String),
  parentLatestName: attr(String),
  createdByUpdate: attr(Boolean),
  deletedByUpdate: attr(Boolean),
  attributeChanges: repeated(CatalogAttributeChange)
});
const Product = new Enum(['SQUARE_POS', 'EXTERNAL_API', 'BILLING', 'APPOINTMENTS', 'INVOICES', 'ONLINE_STORE', 'PAYROLL', 'OTHER', 'DASHBOARD', 'ITEM_LIBRARY_IMPORT']);
const SourceApplication = ProtoBaseModel.extend({
  product: attr(Product),
  applicationId: attr(String),
  name: attr(String)
});
const CatalogUpdate = ProtoBaseModel.extend({
  id: attr(String),
  occurredAt: attr(String),
  source: attr(SourceApplication),
  locationId: attr(String),
  fromSquareSupport: attr(Boolean),
  employeeId: attr(String),
  objectChangesCursor: attr(String),
  objectChanges: repeated(CatalogObjectChange)
});
const SearchCatalogHistoryFilter = ProtoBaseModel.extend({
  objectId: attr(String),
  beginTime: attr(String),
  endTime: attr(String),
  objectTypes: repeated(String),
  sources: attr(SourceApplication),
  employeeIds: attr(String)
});
const SearchCatalogHistoryRequest = ProtoBaseModel.extend({
  filter: attr(SearchCatalogHistoryFilter),
  limit: attr(Number),
  cursor: attr(String)
});
const SearchCatalogHistoryResponse = ProtoBaseModel.extend({
  errors: repeated(ConnectError),
  updates: repeated(CatalogUpdate),
  cursor: attr(String)
});
const ListAuthorizedCustomAttributeDefinitionsRequest = ProtoBaseModel.extend({
  cursor: attr(String),
  includeCounts: attr(Boolean)
});
const CreateCatalogImageRequest = ProtoBaseModel.extend({
  idempotencyKey: attr(String),
  objectId: attr(String),
  image: attr(),
  isObjectImage: attr(Boolean)
});
const CreateCatalogImageResponse = ProtoBaseModel.extend({
  errors: repeated(ConnectError),
  image: attr()
});
const UpdateCatalogImageRequest = ProtoBaseModel.extend({
  idempotencyKey: attr(String),
  imageId: attr(String),
  objectId: attr(String),
  image: attr()
});
const UpdateCatalogImageResponse = ProtoBaseModel.extend({
  errors: repeated(ConnectError),
  image: attr()
});

// replaces placeholders in URL template with attributes from passed request object
// i.e. createRequestURLFromTemplate('/object/{objectId}', { objectId: 'EXAMPLETOKEN'}) => '/object/EXAMPLETOKEN'
const URL_ATTRIBUTE_REGEX = /{(.*)}/g;
function createRequestURLFromTemplate(templateURL, request) {
  return templateURL.replace(URL_ATTRIBUTE_REGEX, (match, attrName) => {
    assert(`missing attribute value ${attrName} required by endpoint with url template ${templateURL}`, isPresent(request[attrName]));
    return request[attrName];
  });
}
function multipartPost(url, requestBody, paramOverrides, multipartData) {
  const fd = new FormData();
  fd.append('request', JSON.stringify(requestBody));
  fd.append('file', multipartData);
  const params = {
    type: 'POST',
    data: fd,
    processData: false,
    contentType: false,
    xhrFields: {
      withCredentials: true
    },
    ...paramOverrides
  };
  return ajax(url, params);
}

// ProtoBaseModel has custom data serialization/deserialization
// endpoints with protobuf request/response classes (ex. SearchCatalogItemVariations) need to transform the request and response data keys
// warning! this does not currently support custom attributes keys (https://prototype.sqprod.co/#/docs/squareup.devscustomattr.service.CustomAttribute#key)
// the key is a string that can contain underscores and hyphens, which will be broken by underscore/camelize
// for context: https://docs.google.com/document/d/1EfTsmMrkVvpKFlKmL9OQfAGrpc6CQpHy6FmIsGTXMTY/edit
function protoPost(url, request, params, responseClass) {
  return jsonPost(url, underscore(request.toJSON()), params).then(resp => {
    return responseClass.fromObject(camelize(resp));
  });
}
function post(url, requestClass, responseClass) {
  return function (options = {}, params = {}, multipartData) {
    const req = requestClass.create(options);
    const formattedUrl = createRequestURLFromTemplate(url, req);

    // Add CSRF header to request params
    params['headers'] = params['headers'] || {};
    params['headers']['X-CSRF-Token'] = getCookie('_js_csrf');
    if (req instanceof SearchCatalogItemVariationsRequest) {
      return protoPost(formattedUrl, req, params, responseClass);
    }
    if (multipartData) {
      return multipartPost(formattedUrl, req.toJSON(), params, multipartData);
    } else {
      return jsonPost(formattedUrl, req.toJSON(), params);
    }
  };
}
function get(url, requestClass) {
  return function (options = {}, ajaxOptions = {}) {
    const req = requestClass.create(options);
    let reqURL = createRequestURLFromTemplate(url, req);
    if (options && Object.keys(options).length > 0) {
      const json = req.toJSON();
      const params = Object.keys(json).reduce((params, key) => {
        const value = json[key];
        if (isPresent(value)) {
          const paramValue = isArray(value) ? value.join(',') : value;
          params.push(`${key}=${paramValue}`);
        }
        return params;
      }, []);
      reqURL = `${reqURL}?${params.join('&')}`;
    }
    return jsonGet(reqURL, ajaxOptions);
  };
}
function put(url, requestClass) {
  return function (options = {}) {
    const req = requestClass.create(options);
    return jsonPut(createRequestURLFromTemplate(url, req), req.toJSON());
  };
}
function deleteFn(url, requestClass) {
  return function (options = {}, ajaxOptions = {}) {
    const req = requestClass.create(options);
    return jsonDelete(createRequestURLFromTemplate(url, req), null, ajaxOptions);
  };
}
const methodsToFunc = {
  POST: post,
  GET: get,
  DELETE: deleteFn,
  PUT: put
};
const endpointDefinitions = {
  listCatalog: {
    request: ListCatalogRequest,
    response: ListCatalogResponse,
    path: '/list',
    method: 'GET'
  },
  retrieveCatalogObject: {
    request: RetrieveCatalogObjectRequest,
    response: RetrieveCatalogObjectResponse,
    path: '/object/{objectId}',
    method: 'GET'
  },
  upsertCatalogObject: {
    request: UpsertCatalogObjectRequest,
    response: UpsertCatalogObjectResponse,
    path: '/object',
    method: 'POST'
  },
  batchUpsertCatalogObjects: {
    request: BatchUpsertCatalogObjectsRequest,
    response: BatchUpsertCatalogObjectsResponse,
    path: '/batch-upsert',
    method: 'POST'
  },
  deleteCatalogObject: {
    request: DeleteCatalogObjectRequest,
    response: DeleteCatalogObjectResponse,
    path: '/object/{objectId}',
    method: 'DELETE'
  },
  batchDeleteCatalogObjects: {
    request: BatchDeleteCatalogObjectsRequest,
    response: BatchDeleteCatalogObjectsResponse,
    path: '/batch-delete',
    method: 'POST'
  },
  batchRetrieveCatalogObjects: {
    request: BatchRetrieveCatalogObjectsRequest,
    response: BatchRetrieveCatalogObjectsResponse,
    path: '/batch-retrieve',
    method: 'POST'
  },
  searchCatalogItems: {
    request: SearchCatalogItemsRequest,
    response: SearchCatalogItemsResponse,
    path: '/search-catalog-items',
    method: 'POST'
  },
  searchCatalogItemVariations: {
    request: SearchCatalogItemVariationsRequest,
    response: SearchCatalogItemVariationsResponse,
    path: '/search-catalog-item-variations',
    method: 'POST'
  },
  searchCatalogObjects: {
    request: SearchCatalogObjectsRequest,
    response: SearchCatalogObjectsResponse,
    path: '/search',
    method: 'POST'
  },
  searchCatalogImages: {
    request: SearchCatalogImagesRequest,
    response: SearchCatalogImagesResponse,
    path: '/images/search',
    method: 'POST',
    useFrontend: false
  },
  searchCatalogHistory: {
    request: SearchCatalogHistoryRequest,
    response: SearchCatalogHistoryResponse,
    path: '/history',
    method: 'POST'
  },
  listAuthorizedCustomAttributeDefinitions: {
    request: ListAuthorizedCustomAttributeDefinitionsRequest,
    response: ListCatalogResponse,
    path: '/list-authorized-custom-attribute-definitions',
    method: 'GET'
  },
  createCatalogImage: {
    request: CreateCatalogImageRequest,
    response: CreateCatalogImageResponse,
    path: '/images',
    method: 'POST',
    useFrontend: false
  },
  updateCatalogImage: {
    request: UpdateCatalogImageRequest,
    response: UpdateCatalogImageResponse,
    path: '/images/{imageId}',
    method: 'POST',
    useFrontend: false
  }
};
function captureSentryError(error) {
  const catalogError = CatalogError.fromXhrError(error);
  sendErrorToSentry(catalogError);
  throw error;
}
function makeClient(endpointDefinitions) {
  return Object.keys(endpointDefinitions).reduce((client, key) => {
    const def = endpointDefinitions[key];
    let url;
    if (def.useFrontend === false) {
      url = baseConnectUrl(`${CATALOG_CONNECT_V2_URL_PREFIX}${def.path}`);
    } else {
      url = `${CATALOG_CONNECT_V2_FRONTEND_URL_PREFIX}${def.path}`;
    }

    // catalog/proactive-dashboard-improvement-release-sentry-error-logging
    client[key] = function (...args) {
      const endpointDefinitionCallback = methodsToFunc[def.method](url, def.request, def.response);
      return endpointDefinitionCallback.apply(this, args).catch(captureSentryError);
    };
    client[key].url = url;
    client[key].method = def.method;
    return client;
  }, {});
}
export const CatalogConnectV2Client = makeClient(endpointDefinitions);